import { ReactElement, Suspense, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { prepareLocationForAccountClose } from 'utils/account/prepare-location-for-account-close';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fireEvent } from 'utils/generic/event-bus';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { ECasinoGameMode, ICasinoCachedGames } from 'interfaces/casino-data';
import { AfterSignIn } from 'services/afterSignIn';
import { UtilsCasino } from 'newelements/Casino/GameSingleViewRenderer/__partials/GameViewContainer/utils';
import { ESelectedActionTypes } from 'newelements/Sportsbook/__partials/SportsbookSportsMenu/types';
import { Loading } from 'components/Loading';
import { addQuery, getSavedLocation, updateQuery } from 'components/QueryRoute';
import { useCasinoSingleGameClose } from 'hooks/casino/useCasinoSingleGameClose';
import { useSearchParams } from 'hooks/useSearchParams';
import { useFetchAccountParams } from 'newhooks/account/useFetchAccountParams';
import { AccountsModalProvider } from 'providers/account/AccountsModalProvider';
import {
  resetPostGridHiddenParams,
  setAfterSignInActions,
  setForgotPasswordOpen,
  setForgotUsernameOpen,
  setHigherZIndex
} from 'store/actions/app-data';
import {
  setFastRegData,
  setShowCancelRegisterPopup
} from 'store/actions/user-data';
import {
  getForgotPasswordOpen,
  getForgotUsernameOpen,
  getHigherZIndex
} from 'store/selectors/app-data';
import { getActiveGames } from 'store/selectors/new-casino';
import { getIsLoggedIn } from 'store/selectors/user-data';

const { MobileLayout } = lazy(
  () => import(/* webpackChunkName: "accounts-mobile-layout" */ './Mobile')
);

const { DesktopLayout } = lazy(
  () => import(/* webpackChunkName: "accounts-desktop-layout" */ './Desktop')
);

type Props = {
  children: ReactElement;
};

export const AccountsLayout = (props: Props): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { accountParams } = useFetchAccountParams();
  const higherZindex = useSelector(getHigherZIndex);
  const { register } = useSearchParams();
  const forgotPass = useSelector(getForgotPasswordOpen);
  const forgotUsername = useSelector(getForgotUsernameOpen);
  const activeGames = useSelector(getActiveGames) as ICasinoCachedGames;
  const { handleCloseClick } = useCasinoSingleGameClose();
  const isLoggedIn = useSelector(getIsLoggedIn);

  const closeModal = useCallback(() => {
    fireEvent({ key: ESelectedActionTypes.CLOSE_ACCOUNT_POPUP });

    const firstActiveGame = Object.values(activeGames)[0];

    if (
      firstActiveGame &&
      (!firstActiveGame.data.types.funMode || !SpringConfigs.CASINO_FUN_MODE)
    ) {
      handleCloseClick();

      return;
    }

    if (forgotPass) {
      dispatch(setForgotPasswordOpen(false));
    }

    if (forgotUsername) {
      dispatch(setForgotUsernameOpen(false));
    }

    if (higherZindex) {
      dispatch(setHigherZIndex(false));
    }

    if (register && Number(accountParams?.exitConfirmPopup)) {
      dispatch(setShowCancelRegisterPopup(true));
    } else {
      const location = decodeURI(getSavedLocation());

      if (location.includes('accounts=') && !location.includes('login=')) {
        history.push(updateQuery({ accounts: undefined, login: undefined }));

        return;
      }

      const fullPath = `${history.location.pathname}${history.location.search}`;
      const decodedPath = decodeURI(fullPath);

      if (location === decodedPath) {
        const searchParams = Object.fromEntries(
          new URLSearchParams(window.location.search)
        );

        if (location.includes('multiview=')) {
          const gamesInfo =
            UtilsCasino.extractGameInfoFromSearchParams(searchParams);

          const queryToUpdate = addQuery({
            [UtilsCasino.encodeGameIdWithPrefix(
              gamesInfo[0].order,
              gamesInfo[0].id
            )]: ECasinoGameMode.FUN,
            [UtilsCasino.encodeGameIdWithPrefix(
              gamesInfo[1].order,
              gamesInfo[1].id
            )]: ECasinoGameMode.FUN,
            accounts: undefined,
            login: undefined
          });

          history.push({
            ...queryToUpdate,
            search: UtilsCasino.normalizeStringifiedQuery(queryToUpdate.search)
          });
        } else {
          const query = {
            accounts: undefined,
            login: undefined,
            ...(searchParams.mode === ECasinoGameMode.REAL && !isLoggedIn
              ? { mode: ECasinoGameMode.FUN }
              : {})
          };

          history.push(addQuery(query));
        }
      } else {
        const normalizedLocation = prepareLocationForAccountClose(location);

        history.push(normalizedLocation);
      }

      SpringConfigs.POSTGRID_API_KEY && dispatch(resetPostGridHiddenParams());
      dispatch(setFastRegData(null));
    }

    const afterSignIn = new AfterSignIn();
    afterSignIn.removeActions();
    dispatch(setAfterSignInActions({}));
  }, [accountParams, register, higherZindex, activeGames]);

  return (
    <AccountsModalProvider>
      {isMobile() ? (
        <Suspense fallback={<Loading />}>
          <MobileLayout closeModal={closeModal}>{props.children}</MobileLayout>
        </Suspense>
      ) : (
        <Suspense fallback={null}>
          <DesktopLayout closeModal={closeModal}>
            {props.children}
          </DesktopLayout>
        </Suspense>
      )}
    </AccountsModalProvider>
  );
};
